import { useReducer, useEffect, useMemo, useRef } from "react";

import { useFormContext, useWatch } from "react-hook-form";
import { graphql, useRelayEnvironment, fetchQuery } from "react-relay";
import { useDebouncedCallback } from "use-debounce";

import { type RulesFilterFormData } from "../components/schema";
import {
  buildFilterOperands,
  buildNotificationRuleFilterInput,
  type FilterFormFields,
} from "../utils/formatNotificationRuleInput";

import type { useNotificationRulePreviewQuery as useNotificationRulePreviewQueryType } from "./__generated__/useNotificationRulePreviewQuery.graphql";

const PROBLEM_MESSAGE_MAP: Record<string, string> = {
  "groupBy must not be empty": "Summary is required",
  "a preview target is required": "Instance, Space and Work Type are required",
};

const useNotificationRulePreviewQuery = graphql`
  query useNotificationRulePreviewQuery($input: NotificationRulePreviewInput!) {
    notificationRulePreview(input: $input) {
      groups {
        ... on NotificationRuleJiraPreviewGroup {
          assignee
          issueTitle
          mostRecentMatch
          policy {
            name
            uuid
          }
          resourceCount
          spaceKey
        }
      }
      problems {
        message
      }
      samplePreview {
        ... on NotificationRuleJiraPreviewSample {
          adfDocument
        }
      }
      totalGroupCount
    }
  }
`;

export interface PreviewGroup {
  spaceKey: string;
  issueTitle: string;
  assignee: string | null | undefined;
  resourceCount: number;
  mostRecentMatch: string;
  policy: { uuid: string; name: string };
}

export interface PreviewResult {
  groups: PreviewGroup[];
  totalGroupCount: number | null | undefined;
  adfDocument: string | null;
  problems: string[];
}

type FetchState =
  | { status: "idle"; lastResult: null }
  | { status: "loading"; lastResult: PreviewResult | null }
  | { status: "success"; result: PreviewResult; lastResult: PreviewResult }
  | { status: "error"; message: string; lastResult: PreviewResult | null };

type FetchAction =
  | { type: "loading" }
  | { type: "success"; result: PreviewResult }
  | { type: "error"; message: string }
  | { type: "reset" };

function fetchReducer(state: FetchState, action: FetchAction): FetchState {
  switch (action.type) {
    case "loading":
      return { status: "loading", lastResult: state.lastResult };
    case "success":
      return {
        status: "success",
        result: action.result,
        lastResult: action.result,
      };
    case "error":
      return {
        status: "error",
        message: action.message,
        lastResult: state.lastResult,
      };
    case "reset":
      return { status: "idle", lastResult: null };
  }
}

export function useNotificationRulePreview() {
  const environment = useRelayEnvironment();
  const { control, getValues } = useFormContext<RulesFilterFormData>();
  const [fetchState, dispatch] = useReducer(fetchReducer, {
    status: "idle",
    lastResult: null,
  });

  // useWatch triggers re-renders (and the effect below) when form values change.
  // getValues() is used inside fetchPreview to get properly-typed form data.
  const watchedValues = useWatch({ control });

  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);

  const fetchPreview = useDebouncedCallback(() => {
    const input = buildNotificationRuleFilterInput(getValues());

    if (!input) {
      dispatch({ type: "reset" });
      return;
    }

    subscriptionRef.current?.unsubscribe();
    subscriptionRef.current = fetchQuery<useNotificationRulePreviewQueryType>(
      environment,
      useNotificationRulePreviewQuery,
      { input },
      { fetchPolicy: "network-only" },
    ).subscribe({
      next(data) {
        const payload = data.notificationRulePreview;
        if (!payload) {
          dispatch({ type: "reset" });
          return;
        }

        const groups: PreviewGroup[] = (payload.groups ?? []).flatMap((g) => {
          if ("spaceKey" in g && g.spaceKey !== undefined) {
            return [
              {
                spaceKey: g.spaceKey,
                issueTitle: g.issueTitle ?? "",
                assignee: g.assignee,
                resourceCount: g.resourceCount ?? 0,
                mostRecentMatch: g.mostRecentMatch ?? "",
                policy: {
                  uuid: g.policy?.uuid ?? "",
                  name: g.policy?.name ?? "",
                },
              },
            ];
          }
          return [];
        });

        const adfDocument =
          payload.samplePreview && "adfDocument" in payload.samplePreview
            ? (payload.samplePreview.adfDocument ?? null)
            : null;

        dispatch({
          type: "success",
          result: {
            groups,
            totalGroupCount: payload.totalGroupCount,
            adfDocument,
            problems: (payload.problems ?? []).map(
              (p) => PROBLEM_MESSAGE_MAP[p.message] ?? p.message,
            ),
          },
        });
      },
      error() {
        dispatch({
          type: "error",
          message: "Failed to load preview. Please try again.",
        });
      },
    });
  }, 500);

  // useWatch returns a deeply-partial type but at runtime the filter arrays
  // are always populated with complete CriteriaFilterValues; cast is safe.
  const filterOperands = useMemo(
    () => buildFilterOperands(watchedValues as unknown as FilterFormFields),
    [watchedValues],
  );
  const hasFilters = filterOperands.length > 0;

  useEffect(() => {
    if (filterOperands.length === 0) {
      // Reset immediately so the UI doesn't show a stale count while the
      // debounce window is still open.
      dispatch({ type: "reset" });
      fetchPreview.cancel();
      subscriptionRef.current?.unsubscribe();
      subscriptionRef.current = null;
      return;
    }

    // Dispatch loading immediately so the UI reflects in-flight state before
    // the debounce fires, preventing stale results from a prior fetch showing
    // through while the next debounce window is still open.
    dispatch({ type: "loading" });
    fetchPreview();
    return () => {
      fetchPreview.cancel();
      subscriptionRef.current?.unsubscribe();
      subscriptionRef.current = null;
    };
  }, [watchedValues, fetchPreview, filterOperands]);

  const freshResult =
    fetchState.status === "success" ? fetchState.result : null;
  const isLoading = fetchState.status === "loading";
  const fetchError = fetchState.status === "error" ? fetchState.message : null;

  // Show stale content while a new fetch is in-flight to avoid a jarring blank/loading flash.
  const result = freshResult ?? (isLoading ? fetchState.lastResult : null);
  // True when loading but stale content is already available to show.
  const isRefreshing = isLoading && fetchState.lastResult !== null;
  const issueCount = result?.totalGroupCount ?? result?.groups.length ?? 0;

  return {
    hasFilters,
    result,
    isLoading,
    isRefreshing,
    fetchError,
    issueCount,
  };
}
